import { useEffect } from 'react'
import { Helmet } from 'react-helmet-async'
import { MapPin, Users, Globe2 } from 'lucide-react'
import JsonLd from '../components/JsonLd'
import { BRAND } from '../theme/brand'

const IFRAME_SRC =
  'https://www.europamundo.com/eng/embed/multisearch.aspx?opeIP=519&ageKEY=48307'
const RESIZE_SCRIPT =
  'https://www.europamundo.com/embed_v2/assets/scripts/iframeResizeHeight.min.js'

export default function Europamundo() {
  useEffect(() => {
    if (document.querySelector(`script[src="${RESIZE_SCRIPT}"]`)) return

    const script = document.createElement('script')
    script.type = 'text/javascript'
    script.src = RESIZE_SCRIPT
    document.body.appendChild(script)

    return () => {
      script.remove()
    }
  }, [])

  return (
    <div className="bg-white">
      <Helmet>
        <title>Europe Tours | Europamundo Vacations | 2Life Travel</title>
        <meta
          name="description"
          content="Explore guided Europe tours with Europamundo Vacations. Search itineraries across multiple countries, compare departures, and plan your next European adventure with 2Life Travel."
        />
        <link
          rel="canonical"
          href={typeof window !== 'undefined' ? window.location.origin + '/europamundo' : '/europamundo'}
        />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Europe Tours | Europamundo Vacations | 2Life Travel" />
        <meta
          property="og:description"
          content="Discover flexible, guided coach tours across Europe with Europamundo Vacations."
        />
        <meta
          property="og:url"
          content={typeof window !== 'undefined' ? window.location.href : '/europamundo'}
        />
        <meta property="og:image" content="/2life-logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
      </Helmet>
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'Europamundo Vacations',
          url: typeof window !== 'undefined' ? window.location.href : '/europamundo',
          description:
            'Search and compare guided Europe tours with Europamundo Vacations through 2Life Travel.',
        }}
      />

      {/* Hero */}
      <div
        className="relative py-16 sm:py-20 overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${BRAND.dark} 0%, ${BRAND.maroon} 100%)`,
        }}
      >
        <div className="absolute inset-0 opacity-5">
          <div
            className="absolute inset-0"
            style={{
              backgroundImage: 'radial-gradient(circle at 1px 1px, white 1px, transparent 0)',
              backgroundSize: '40px 40px',
            }}
          />
        </div>
        <div className="relative z-10 mx-auto max-w-4xl px-6 text-center">
          <p
            className="text-sm tracking-[0.3em] uppercase mb-4"
            style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.gold, fontWeight: 500 }}
          >
            Guided Europe Tours
          </p>
          <h1
            className="text-4xl sm:text-5xl font-light text-white leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif" }}
          >
            Europamundo <span className="italic font-medium" style={{ color: BRAND.gold }}>Vacations</span>
          </h1>
          <p
            className="mt-5 text-base text-white/70 max-w-2xl mx-auto leading-relaxed"
            style={{ fontFamily: "'Montserrat', sans-serif", fontWeight: 300 }}
          >
            Flexible, expertly guided coach tours across Europe and various other countries — search itineraries, compare
            departures, and find the journey that suits your style.
          </p>
        </div>
      </div>

      {/* Product write-up */}
      <div className="py-14 sm:py-16" style={{ backgroundColor: BRAND.cream }}>
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-12">
            <h2
              className="text-3xl font-light mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}
            >
              Why <span className="italic font-medium" style={{ color: BRAND.maroon }}>Europamundo</span>?
            </h2>
            <p
              className="text-sm leading-relaxed"
              style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 300 }}
            >
              Europamundo Vacations is one of Europe&apos;s leading guided tour operators, offering
              carefully crafted itineraries that combine iconic cities, cultural highlights, and
              authentic local experiences. Tours are designed for travellers who want the freedom to
              explore at their own pace while enjoying the convenience of pre-arranged transport,
              accommodation, and expert guidance. Whether you prefer a classic grand tour or a
              shorter regional escape, Europamundo makes it easy to discover Europe with confidence.
            </p>
          </div>

          <div className="grid grid-cols-1 gap-6 sm:grid-cols-3 max-w-4xl mx-auto">
            {[
              {
                icon: Globe2,
                title: 'Multi-Country Itineraries',
                text: 'Visit several destinations in one seamless journey across Europe.',
              },
              {
                icon: MapPin,
                title: 'Iconic & Hidden Gems',
                text: 'From famous landmarks to charming towns off the beaten path.',
              },
              {
                icon: Users,
                title: 'Flexible Departures',
                text: 'Compare dates, durations, and tour styles to match your travel plans.',
              },
            ].map((item) => (
              <div
                key={item.title}
                className="p-6 text-center"
                style={{
                  backgroundColor: '#FFFFFF',
                  borderRadius: '2px',
                  borderTop: `3px solid ${BRAND.gold}`,
                }}
              >
                <item.icon className="h-6 w-6 mx-auto mb-3" style={{ color: BRAND.maroon }} />
                <h3
                  className="text-base font-medium mb-2"
                  style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.dark }}
                >
                  {item.title}
                </h3>
                <p
                  className="text-sm leading-relaxed"
                  style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 300 }}
                >
                  {item.text}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Search iframe */}
      <div className="pb-20 sm:pb-24">
        <div className="mx-auto max-w-7xl px-6 lg:px-8">
          <p
            className="text-sm tracking-[0.2em] uppercase mb-2 text-center"
            style={{ fontFamily: "'Montserrat', sans-serif", color: BRAND.taupe, fontWeight: 500 }}
          >
            Search Tours
          </p>
          <h2
            className="text-2xl sm:text-3xl font-light text-center mb-8"
            style={{ fontFamily: "'Cormorant Garamond', serif", color: BRAND.dark }}
          >
            Find Your <span className="italic font-medium" style={{ color: BRAND.maroon }}>European</span> Adventure
          </h2>

          <div
            className="overflow-hidden"
            style={{
              borderRadius: '2px',
              border: `1px solid ${BRAND.taupeLight}`,
              backgroundColor: '#FFFFFF',
            }}
          >
            <iframe
              id="frame_cmp"
              title="Europamundo Vacations tour search"
              src={IFRAME_SRC}
              frameBorder={0}
              scrolling="auto"
              data-opeid="519"
              data-ageid="48307"
              style={{
                width: '100%',
                overflowX: 'hidden',
                minHeight: '400px',
                border: 'none',
                display: 'block',
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
